import React, { useEffect, useRef, useState } from 'react'
import NextImage from 'next/image'
import styles from './styles.module.scss'
import {
  documentToReactComponents,
  Options
} from '@contentful/rich-text-react-renderer'
import { BLOCKS, Document, MARKS, INLINES } from '@contentful/rich-text-types'
import { ButtonLink } from '@components/ui/Button'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'

const Bold = ({ children }: { children: React.ReactNode }) => (
  <strong>{children}</strong>
)

function RenderText(doc: Document, isMobile: boolean) {
  const options: Options = {
    renderMark: {
      [MARKS.BOLD]: text => <Bold>{text}</Bold>
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        // children is already an array of properly rendered inline nodes
        // (text + marks + inline entries)
        if (!children || (Array.isArray(children) && children.length === 0))
          return null
        return <p>{children}</p>
      },

      [INLINES.EMBEDDED_ENTRY]: node => {
        const { buttonStyle, text, uri } = node.data.target.fields || {}
        return (
          <ButtonLink
            key={uri}
            href={uri}
            variant={isMobile ? 'primary_outline' : buttonStyle}
            classes={styles.FeatureCards__contentButton}
          >
            {text}
          </ButtonLink>
        )
      },

      [BLOCKS.EMBEDDED_ASSET]: node => {
        const { url, details, contentType } = node.data.target.fields.file

        if (contentType === 'image/svg+xml') {
          return (
            <div
              className={styles.svgIcon}
              key={url}
              style={
                {
                  maskImage: `url(${url})`,
                  WebkitMaskImage: `url(${url})`,
                  '--svgIcon-width': `${details.image.width}px`,
                  '--svgIcon-height': `${details.image.height}px`
                } as React.CSSProperties
              }
            />
          )
        }

        return (
          <NextImage
            src={url}
            alt={node.data.target.fields.title}
            width={details.image.width}
            height={details.image.height}
            key={url}
          />
        )
      }
    }
  }

  return documentToReactComponents(doc, options)
}

type ImageCopySectionCopyProps = {
  copy: Document[]
  children?: React.ReactNode
}

export default function ImageCopySectionCopy({
  copy,
  children
}: ImageCopySectionCopyProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const isMobile = useMediaQuery({
    maxWidth: BREAKPOINT_SIZES.DESKTOP,
    initialState: true
  })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    function checkMaskVisibility() {
      if (!el) return

      const isOverflowing = el.scrollHeight > el.clientHeight
      const isAtBottom =
        Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) < 1

      if (isOverflowing && !isAtBottom) {
        setIsOverflowing(true)
      } else {
        setIsOverflowing(false)
      }
    }

    // Initial check
    checkMaskVisibility()

    // Listen to scroll
    el.addEventListener('scroll', checkMaskVisibility)

    // Listen to window resize
    window.addEventListener('resize', checkMaskVisibility)

    // MutationObserver for dynamic content changes
    const observer = new MutationObserver(checkMaskVisibility)
    observer.observe(el, {
      childList: true,
      subtree: true,
      characterData: true
    })

    return () => {
      el.removeEventListener('scroll', checkMaskVisibility)
      window.removeEventListener('resize', checkMaskVisibility)
      observer.disconnect()
    }
  }, [])

  return (
    <div className={styles.ImageCopySectionCopy}>
      <div
        className={`${styles.ImageCopySectionCopy__wrapper} ${
          isOverflowing ? styles.overflowing : ''
        }`}
        ref={containerRef}
      >
        {copy.map((node, i) => (
          <React.Fragment key={i}>{RenderText(node, isMobile)}</React.Fragment>
        ))}
      </div>
      {children}
    </div>
  )
}
